// Generate code samples per language untuk satu operation.
// Languages: cURL, JavaScript (fetch), Python (requests), PHP (curl), Go (net/http).

import type { Operation } from './types';

const SERVER = 'https://api.satutim.id';

interface SnippetCtx {
    method: string;
    path: string;
    apiKey?: string;
    sampleBody?: unknown;
}

function buildUrl(path: string, _op?: Operation): string {
    // Path params disubstitusi dengan placeholder {name} — user akan replace di Try-it
    return SERVER + path;
}

export function curlSnippet({ method, path, apiKey, sampleBody }: SnippetCtx): string {
    const url = buildUrl(path);
    const lines = [`curl --request ${method} \\`, `    --url ${url} \\`];
    lines.push(`    --header 'Authorization: Bearer ${apiKey || '<YOUR_API_KEY>'}' \\`);
    if (sampleBody && method !== 'GET' && method !== 'DELETE') {
        lines[lines.length - 1] = lines[lines.length - 1].replace(/ \\$/, ' \\');
        lines.push(`    --header 'Content-Type: application/json' \\`);
        lines.push(`    --data '${JSON.stringify(sampleBody, null, 2)}'`);
    } else {
        lines[lines.length - 1] = lines[lines.length - 1].replace(/ \\$/, '');
    }
    return lines.join('\n');
}

export function jsSnippet({ method, path, apiKey, sampleBody }: SnippetCtx): string {
    const url = buildUrl(path);
    const hasBody = sampleBody && method !== 'GET' && method !== 'DELETE';
    return `const response = await fetch('${url}', {
    method: '${method}',
    headers: {
        'Authorization': 'Bearer ${apiKey || '<YOUR_API_KEY>'}',${hasBody ? `\n        'Content-Type': 'application/json',` : ''}
    },${hasBody ? `\n    body: JSON.stringify(${JSON.stringify(sampleBody, null, 4).split('\n').map((l, i) => i === 0 ? l : '    ' + l).join('\n')}),` : ''}
});

const data = await response.json();
console.log(data);`;
}

export function pythonSnippet({ method, path, apiKey, sampleBody }: SnippetCtx): string {
    const url = buildUrl(path);
    const hasBody = sampleBody && method !== 'GET' && method !== 'DELETE';
    const methodLower = method.toLowerCase();
    return `import requests

headers = {
    "Authorization": "Bearer ${apiKey || '<YOUR_API_KEY>'}",
}
${hasBody ? `\nbody = ${JSON.stringify(sampleBody, null, 4)}\n` : ''}
response = requests.${methodLower}(
    "${url}",
    headers=headers,${hasBody ? '\n    json=body,' : ''}
)

print(response.json())`;
}

export function phpSnippet({ method, path, apiKey, sampleBody }: SnippetCtx): string {
    const url = buildUrl(path);
    const hasBody = sampleBody && method !== 'GET' && method !== 'DELETE';
    return `<?php
$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, "${url}");
curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "${method}");
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    "Authorization: Bearer ${apiKey || '<YOUR_API_KEY>'}",${hasBody ? `\n    "Content-Type: application/json",` : ''}
]);${hasBody ? `\ncurl_setopt($ch, CURLOPT_POSTFIELDS, '${JSON.stringify(sampleBody)}');` : ''}
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
curl_close($ch);
echo $response;`;
}

export function goSnippet({ method, path, apiKey, sampleBody }: SnippetCtx): string {
    const url = buildUrl(path);
    const hasBody = sampleBody && method !== 'GET' && method !== 'DELETE';
    return `package main

import (
    "fmt"
    "io"
    "net/http"${hasBody ? `\n    "strings"` : ''}
)

func main() {
    ${hasBody ? `body := strings.NewReader(\`${JSON.stringify(sampleBody)}\`)\n    ` : ''}req, _ := http.NewRequest("${method}", "${url}", ${hasBody ? 'body' : 'nil'})
    req.Header.Set("Authorization", "Bearer ${apiKey || '<YOUR_API_KEY>'}")
    ${hasBody ? `req.Header.Set("Content-Type", "application/json")\n    ` : ''}
    resp, _ := http.DefaultClient.Do(req)
    defer resp.Body.Close()
    out, _ := io.ReadAll(resp.Body)
    fmt.Println(string(out))
}`;
}

export const LANGUAGES = [
    { id: 'curl', label: 'cURL', shikiLang: 'bash', fn: curlSnippet },
    { id: 'js', label: 'JavaScript', shikiLang: 'javascript', fn: jsSnippet },
    { id: 'python', label: 'Python', shikiLang: 'python', fn: pythonSnippet },
    { id: 'php', label: 'PHP', shikiLang: 'php', fn: phpSnippet },
    { id: 'go', label: 'Go', shikiLang: 'go', fn: goSnippet },
] as const;
